import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { useLanguage } from '../LanguageContext';
import './Dashboard.css';

function Dashboard() {
  const { user, authFetch } = useAuth();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await authFetch('/api/auth/profile');
        const data = await res.json();
        if (res.ok) {
          setProfile(data);
        }
      } catch (err) {
        console.error('Failed to fetch profile:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [authFetch]);

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  const customer = profile?.customer;
  const displayName = customer?.is_legal_entity
    ? (customer?.company_name || t('dashboard.defaultUser'))
    : (customer?.first_name || t('dashboard.defaultUser'));
  const isIncomplete = customer?.status === 'incomplete';

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div className="welcome-section">
          <h1>{t('dashboard.welcomeTitle', { name: displayName })}</h1>
          <p>{t('dashboard.welcomeSubtitle')}</p>
        </div>

        <div className="status-badge-container">
          <span className={`status-badge status-${customer?.status}`}>
            {customer?.status === 'verified' ? t('dashboard.statusVerified') :
             customer?.status === 'pending' ? t('dashboard.statusPending') :
             customer?.status === 'incomplete' ? t('dashboard.statusIncomplete') :
             customer?.status === 'suspended' ? t('dashboard.statusSuspended') : customer?.status}
          </span>
        </div>
      </div>

      {/* CTA: prompt incomplete customers to complete KYC */}
      {isIncomplete && (
        <div className="kyc-cta">
          <div className="kyc-cta-content">
            <h2>{t('dashboard.kycCtaTitle')}</h2>
            <p>{t('dashboard.kycCtaText')}</p>
          </div>
          <button className="btn btn-primary" onClick={() => navigate('/verification')}>
            {t('dashboard.kycCtaButton')}
          </button>
        </div>
      )}

      {/* Quick Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">🔒</div>
          <div className="stat-content">
            <div className="stat-value">{customer?.verified ? t('common.yes') : t('common.no')}</div>
            <div className="stat-label">{t('dashboard.verifiedAccountLabel')}</div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="section">
        <h2>{t('dashboard.quickActionsTitle')}</h2>
        <div className="quick-actions">
          <a href="https://zlatarenta.com" className="action-card" target="_blank" rel="noopener noreferrer">
            <div className="action-icon">🏆</div>
            <div className="action-content">
              <h3>{t('dashboard.buyGoldTitle')}</h3>
              <p>{t('dashboard.buyGoldText')}</p>
            </div>
            <div className="action-arrow">→</div>
          </a>

          <Link to="/profile" className="action-card">
            <div className="action-icon">👤</div>
            <div className="action-content">
              <h3>{t('dashboard.editProfileTitle')}</h3>
              <p>{t('dashboard.editProfileText')}</p>
            </div>
            <div className="action-arrow">→</div>
          </Link>
        </div>
      </div>

      {/* Saved KYC Data Summary */}
      {!isIncomplete && customer && (
        <div className="section">
          <h2>{t('dashboard.savedDataTitle')}</h2>
          <p className="section-description">
            {t('dashboard.savedDataDescription')}
          </p>

          <div className="data-grid">
            <div className="data-card">
              <h3>{t('dashboard.documentSectionTitle')}</h3>
              <div className="data-item">
                <span className="data-label">{t('dashboard.typeLabel')}</span>
                <span className="data-value">
                  {customer.id_doc_type === 'osebna' ? t('dashboard.idDocTypeIdCard') :
                   customer.id_doc_type === 'potni_list' ? t('dashboard.idDocTypePassport') : '-'}
                </span>
              </div>
              <div className="data-item">
                <span className="data-label">{t('dashboard.numberLabel')}</span>
                <span className="data-value">{customer.id_doc_number || '-'}</span>
              </div>
              <div className="data-item">
                <span className="data-label">{t('dashboard.validUntilLabel')}</span>
                <span className="data-value">
                  {customer.id_doc_permanent ? t('dashboard.permanentLabel') :
                   customer.id_doc_expiry_date
                    ? new Date(customer.id_doc_expiry_date).toLocaleDateString(language === 'sl' ? 'sl-SI' : 'en-GB')
                    : '-'}
                </span>
              </div>
            </div>

            <div className="data-card">
              <h3>{t('dashboard.basicDataSectionTitle')}</h3>
              <div className="data-item">
                <span className="data-label">{t('dashboard.taxNumberLabel')}</span>
                <span className="data-value">{customer.tax_number || '-'}</span>
              </div>
              <div className="data-item">
                <span className="data-label">{t('dashboard.emsoLabel')}</span>
                <span className="data-value">{customer.emso || '-'}</span>
              </div>
              <div className="data-item">
                <span className="data-label">{t('dashboard.addressLabel')}</span>
                <span className="data-value">
                  {customer.address_city ? `${customer.address_postal_code || ''} ${customer.address_city}` : '-'}
                </span>
              </div>
              <div className="data-item">
                <span className="data-label">{t('dashboard.citizenshipLabel')}</span>
                <span className="data-value">{customer.citizenship || '-'}</span>
              </div>
            </div>

            <div className="data-card">
              <h3>{t('dashboard.statusSectionTitle')}</h3>
              <div className="data-item">
                <span className="data-label">{t('dashboard.dualCitizenshipLabel')}</span>
                <span className="data-value">{customer.dual_citizenship ? t('common.yes') : t('common.no')}</span>
              </div>
              <div className="data-item">
                <span className="data-label">{t('dashboard.sanctionsLabel')}</span>
                <span className="data-value">{customer.sanctions_connection ? t('common.yes') : t('common.no')}</span>
              </div>
              <div className="data-item">
                <span className="data-label">{t('dashboard.pepLabel')}</span>
                <span className="data-value">{customer.pep_status ? t('common.yes') : t('common.no')}</span>
              </div>
            </div>

            <div className="data-card">
              <h3>{t('dashboard.sourceOfFundsSectionTitle')}</h3>
              <div className="data-item sources">
                {customer.source_of_funds?.length > 0 ? (
                  customer.source_of_funds.map((source, i) => (
                    <span key={i} className="source-tag">{source}</span>
                  ))
                ) : (
                  <span className="data-value">-</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* How it Works */}
      <div className="section info-section">
        <h2>{t('dashboard.howItWorksTitle')}</h2>
        <div className="steps">
          <div className="step-item">
            <div className="step-number">1</div>
            <div className="step-content">
              <h4>{t('dashboard.step1Title')}</h4>
              <p>{t('dashboard.step1Text')}</p>
            </div>
          </div>
          <div className="step-item">
            <div className="step-number">2</div>
            <div className="step-content">
              <h4>{t('dashboard.step2Title')}</h4>
              <p>{t('dashboard.step2Text')}</p>
            </div>
          </div>
          <div className="step-item">
            <div className="step-number">3</div>
            <div className="step-content">
              <h4>{t('dashboard.step3Title')}</h4>
              <p>{t('dashboard.step3Text')}</p>
            </div>
          </div>
          <div className="step-item">
            <div className="step-number">4</div>
            <div className="step-content">
              <h4>{t('dashboard.step4Title')}</h4>
              <p>{t('dashboard.step4Text')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;

